import type { components as ContentSchemas } from '@core/types/content-shapes'
import type { ResolvableLink, ResolvableMeta, ResolvableScript, UseSeoMetaInput } from '@unhead/vue'
import { useRequestURL, useRoute, useContentMedia, useCoreConfig, useSiteUrl } from '#imports'
import { resolveMediaUrl } from '@nimbus/utils/resolveMediaUrl'

const OG_IMAGE_PATH = '/cdn-cgi/image/fit=scale-down,format=webp,quality=64,width=1200,height=630/media/logo_opengraph.jpg?v=2'

type SeoData = Omit<ContentSchemas['schemas']['SharedSeoComponent'], 'metaDescription'> & {
    /** `null` clears the description tags a layout set. */
    metaDescription?: string | null
}

// Not reactive due to issues with memory leaks
export const useContentSeo = (seoData?: SeoData) => {
    const route = useRoute()
    const { locale } = useCoreConfig()
    const siteUrl = useSiteUrl()

    function getHead() {
        if (!seoData) return {}

        const script: ResolvableScript[] = []
        if (seoData.structuredData) {
            script.push({
                type: 'application/ld+json',
                innerHTML: JSON.stringify(seoData.structuredData),
            })
        }

        // unhead v3 dropped `keywords` from useSeoMeta's input, so it ships as a plain meta tag.
        const meta: ResolvableMeta[] = []
        if (seoData.keywords) {
            meta.push({
                name: 'keywords',
                content: seoData.keywords,
            })
        }

        const link: ResolvableLink[] = []

        if (seoData.canonicalURL) {
            link.push({
                rel: 'canonical',
                href: `${seoData.canonicalURL}`,
            })
        }
        else if (Object.keys(route.query).length) {
            link.push({
                rel: 'canonical',
                href: siteUrl(route.path) ?? route.path,
            })
        }

        return {
            script,
            link,
            meta,
        }
    }

    function getSeoMeta(): UseSeoMetaInput {
        const request = useRequestURL()
        const { getContentMediaUrl } = useContentMedia()

        const defaults = {
            ogType: 'article' as const,
            ogLocale: locale.replace('-', '_'),
            ogUrl: siteUrl(route.path) ?? `${request.origin}${request.pathname}`,
            ogImageUrl: siteUrl(OG_IMAGE_PATH) ?? `${request.origin}${OG_IMAGE_PATH}`,
            ogImageWidth: 1200,
            ogImageHeight: 630
        }

        if (!seoData) return defaults

        const imageUrl = seoData.metaImage?.url ? resolveMediaUrl(seoData.metaImage.url, getContentMediaUrl) : undefined
        // Crawlers need an absolute og:image; local media is served from the site itself.
        const absoluteImageUrl = imageUrl?.startsWith('/') ? siteUrl(imageUrl) ?? `${request.origin}${imageUrl}` : imageUrl

        const og = seoData.metaSocial?.find((x: any) => x.socialNetwork === 'Facebook')
        const twitter = seoData.metaSocial?.find((x: any) => x.socialNetwork === 'Twitter')

        return {
            title: seoData?.metaTitle as string,
            description: seoData?.metaDescription,
            ogTitle: og?.title || seoData?.metaTitle,
            ogDescription: og?.description || seoData?.metaDescription,
            twitterTitle: twitter?.title || seoData?.metaTitle,
            twitterDescription: twitter?.description || seoData?.metaDescription,
            // An empty field is null, and `useSeoMeta` removes a null tag: the lobbies' `noindex`.
            robots: seoData?.metaRobots ?? undefined,

            ogType: defaults.ogType,
            ogLocale: defaults.ogLocale,
            ogUrl: defaults.ogUrl,
            // The default size describes the default image only; an image of unknown size gets none.
            ogImageUrl: absoluteImageUrl || defaults.ogImageUrl,
            ogImageWidth: absoluteImageUrl ? seoData.metaImage?.width ?? undefined : defaults.ogImageWidth,
            ogImageHeight: absoluteImageUrl ? seoData.metaImage?.height ?? undefined : defaults.ogImageHeight,
        }
    }

    return {
        getHead,
        getSeoMeta,
    }
}
